import { useEffect, useReducer } from 'react';

import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { MaskedTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { AxiosError } from 'axios';
import moment from 'moment';
import { Col, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { requests } from 'common/axios';
import { ControlledSyncfusionDatePicker } from 'components/Common/SyncfusionWrappers/Syncfusion';
import { AdditionalContact } from 'components/ProviderData/IpaAdministration/atoms/Models';
import {
    initialClaimDepartmentFormState,
    updateClaimDepartmentFormState,
} from 'components/ProviderData/IpaAdministration/organisms/ClaimDepartment/ClaimDepartmentForm.state';
import { shouldTriggerIdCardWorkflow } from 'components/ProviderData/IpaAdministration/organisms/ClaimDepartment/ClaimDepartmentForm.utils';
import {
    ClaimDepartmentResponse,
    claimDepartmentService,
} from 'components/ProviderData/subjects/IpaClaimDepartmentService';
import { CLAIM_INFO_CODE } from 'components/ProviderData/utils/Constants';
import DisabledWrapper from 'components/ProviderData/utils/DisabledWrapper';
import { dialogService } from 'subjects/common/DialogService';

export const ClaimDepartmentForm = ({ contact, id, onFormDirtyChange, isReadOnlyViewer, risk }) => {
    const {
        control,
        register,
        handleSubmit,
        reset,
        setValue,
        getValues,
        formState: { errors, isDirty },
        watch,
        trigger,
    } = useForm<any>({
        defaultValues: {
            misDirectedPayerId: '',
            tradingPartnerId: '',
            canAcceptEditInboundOutbound: false,
            canAcceptClaimTypes: false,
            endDt: new Date(9999, 11, 31),
        },
    });

    const [state, dispatch] = useReducer(updateClaimDepartmentFormState, initialClaimDepartmentFormState);

    const saveInformation = async (data: AdditionalContact) => {
        try {
            dialogService.loading(true);
            let action = 'AddAdditionalContact';
            const adaptedData = {
                ...data,
                id: contact?.id ? contact?.id : 0,
                operationType: null,
                department: CLAIM_INFO_CODE,
                misDirectedPayerId: data.misDirectedPayerId ? data.misDirectedPayerId : null,
                startDt: moment(data?.startDt).format('MM/DD/YYYY'),
                endDt: moment(data?.endDt).format('MM/DD/YYYY'),
            };

            adaptedData.claimAddress = adaptedData?.claimAddress?.trim() ?? '';
            if (adaptedData?.claimAddress2 && adaptedData?.claimAddress2?.length > 0) {
                adaptedData.claimAddress += `; ${adaptedData.claimAddress2.trim()}`;
            }

            if (contact?.id) {
                action = 'UpdateAdditionalContact';
            }
            adaptedData.ipaWeb_Id = id;
            delete adaptedData['claimAddress2'];
            const url = `/api/AdditionalContact/${action}`;
            const response = (await requests.post(url, adaptedData)) as ClaimDepartmentResponse;
            response.id = id;
            if (shouldTriggerIdCardWorkflow(data, contact)) {
                response.triggerIdCardWorkflow = true;
            }
            claimDepartmentService.setResponse(response);
        } catch (e) {
            const message = `${(e as AxiosError)?.response?.data}`;
            claimDepartmentService.setResponse({ error: true, message, id });
        } finally {
            dialogService.loading(false);
            dialogService.close();
        }
    };

    useEffect(() => {
        dispatch({ type: 'Department', payload: CLAIM_INFO_CODE });

        const subscription = claimDepartmentService.submit$().subscribe(() => {
            handleSubmit(saveInformation)();
        });
        if (contact.id) {
            for (const c in getValues()) {
                setValue(c, contact[c]);
            }
        } else {
            reset();
        }
        return () => {
            subscription.unsubscribe();
        };
    }, [contact]);

    useEffect(() => {
        onFormDirtyChange(isDirty);
    }, [watch]);

    return (
        <DisabledWrapper disabled={isReadOnlyViewer}>
            <form>
                <Row className='mb-3'>
                    <Col md='6'>
                        <div className='no-padding'>
                            <label className='e-label-select mb-2'>Start Date *</label>
                            <ControlledSyncfusionDatePicker
                                format='MM/dd/yyyy'
                                name='startDt'
                                control={control}
                                dpAttrs={{
                                    showClearButton: true,
                                    placeholder: 'Start date',
                                    min: undefined,
                                    max: new Date(9999, 11, 31),
                                    strictMode: true,
                                    enableMask: true,
                                }}
                                onChangeHandler={({ valueAsDate }: { valueAsDate: Date }) => {
                                    dispatch({ type: 'StartDate', payload: valueAsDate });
                                }}
                                isRetrievingDateAsString={true}
                                rules={{
                                    required: 'Start date is required',
                                }}
                            />
                            {errors.startDt && <p className='error-form'>{errors.startDt.message}</p>}
                        </div>
                    </Col>
                    <Col md='6'>
                        <div className='no-padding'>
                            <label className='e-label-select mb-2'>Term. Date *</label>
                            <ControlledSyncfusionDatePicker
                                format='MM/dd/yyyy'
                                name='endDt'
                                control={control}
                                dpAttrs={{
                                    showClearButton: true,
                                    placeholder: 'End date',
                                    min: state.startDate,
                                    max: new Date(9999, 11, 31),
                                    strictMode: true,
                                    enableMask: true,
                                }}
                                isRetrievingDateAsString={true}
                                rules={{
                                    required: 'End date is required',
                                }}
                            />
                            {errors.endDt && <p className='error-form'>{errors.endDt.message}</p>}
                        </div>
                    </Col>
                </Row>
                <Row>
                    {state.department === CLAIM_INFO_CODE && risk && risk !== 'C' && risk !== 'F' && (
                        <Col md='12'>
                            <div>
                                <div className={`e-float-input e-input-group`}>
                                    <input
                                        type='text'
                                        {...register('claimAddress', {
                                            validate: (value) => {
                                                if (value === '' && state.claimAddressControlFlag === true) {
                                                    return 'Claim Address is Required';
                                                }
                                            },
                                            maxLength: {
                                                value: 250,
                                                message: 'Claim Address 1 should be maximum 250 characters',
                                            },
                                        })}
                                        onChange={(obj) => {
                                            setValue('claimAddress', obj.currentTarget.value);
                                            trigger(['claimAddress']);
                                        }}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>
                                        Claim Address 1 {state.claimAddressControlFlag === true && '*'}
                                    </label>
                                </div>
                                {errors.claimAddress && <p className='error-form'>{errors.claimAddress.message}</p>}
                            </div>
                            <div>
                                <div className={`e-float-input e-input-group`}>
                                    <input
                                        type='text'
                                        {...register('claimAddress2', {
                                            maxLength: {
                                                value: 250,
                                                message: 'Claim Address 2 should be maximum 250 characters',
                                            },
                                        })}
                                        onChange={(obj) => {
                                            setValue('claimAddress2', obj.currentTarget.value);
                                            trigger(['claimAddress2']);
                                        }}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>
                                        Claim Address 2 {state.claimAddressControlFlag === true && '*'}
                                    </label>
                                </div>
                                {errors.claimAddress2 && <p className='error-form'>{errors.claimAddress2.message}</p>}
                            </div>
                            <div className='mt-3'>
                                <CheckBoxComponent
                                    {...register('isClaimAddress')}
                                    checked={state.isClaimAddressFlag}
                                    label='Is Claim Address Display on IDCard'
                                    change={({ checked }) => {
                                        dispatch({ type: 'IsClaimAddressFlag', payload: checked });
                                        setValue('isClaimAddress', checked);
                                        dispatch({ type: 'ClaimAddressControlFlag', payload: checked });
                                        trigger(['claimAddress']);
                                    }}
                                />
                            </div>
                        </Col>
                    )}
                </Row>

                {state.department === 'CLM' ? (
                    <div>
                        <Row>
                            <Col md={6}>
                                <div className={`e-float-input e-input-group`}>
                                    <input
                                        type='text'
                                        {...register('misDirectedPayerId', {
                                            maxLength: {
                                                value: 8,
                                                message: 'Misdirected Payer ID should be maximum 8 characters',
                                            },
                                        })}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>Misdirected Payer ID</label>
                                </div>
                                {errors?.misDirectedPayerId?.message && (
                                    <p className='error-form'>{errors.misDirectedPayerId.message}</p>
                                )}
                            </Col>
                            <Col md={6}>
                                <div className={`e-float-input e-input-group`}>
                                    <input
                                        type='text'
                                        {...register('tradingPartnerId', {
                                            maxLength: {
                                                value: 100,
                                                message: 'Trading Partner Id should be maximum 100 characters',
                                            },
                                        })}
                                    />
                                    <span className='e-float-line'></span>
                                    <label className='e-float-text'>Trading Partner ID </label>
                                </div>
                                {errors?.tradingPartnerId?.message && (
                                    <p className='error-form'>{errors.tradingPartnerId.message}</p>
                                )}
                            </Col>
                        </Row>
                        <Row className='mt-3'>
                            <Col md={6}>
                                <CheckBoxComponent
                                    {...register('canAcceptEditInboundOutbound')}
                                    checked={contact.canAcceptEditInboundOutbound}
                                    label='Accept EDI for Inbound and Outbound'
                                    change={({ checked }) => setValue('canAcceptEditInboundOutbound', checked)}
                                />
                            </Col>
                            <Col md={6}>
                                <CheckBoxComponent
                                    {...register('canAcceptClaimTypes')}
                                    checked={contact.canAcceptClaimTypes}
                                    label='Accept Claim Types'
                                    change={({ checked }) => setValue('canAcceptClaimTypes', checked)}
                                />
                            </Col>
                        </Row>
                    </div>
                ) : null}
                <Row className='mt-4'>
                    <Col md={6}>
                        <label>
                            <b>Primary Contact</b>
                        </label>
                        <div className='mt-3'>
                            <label className='e-label-select mb-2'>Title</label>
                            <div
                                className={`e-float-input e-input-group mb-3`}
                                style={{ marginTop: '0px' }}
                            >
                                <input
                                    type='text'
                                    {...register('primaryContactTitle', {
                                        maxLength: {
                                            value: 240,
                                            message: 'Primary Contact Title should be maximum 240 characters',
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                            </div>
                            {errors?.primaryContactTitle?.message && (
                                <p className='error-form'>{errors.primaryContactTitle.message}</p>
                            )}
                            <label className='e-label-select mb-2'>Name</label>
                            <div
                                className={`e-float-input e-input-group ${errors.primaryContactName ? 'mb-1' : 'mb-3'}`}
                                style={{ marginTop: '0px' }}
                            >
                                <input
                                    type='text'
                                    {...register('primaryContactName', {
                                        required: 'Name is required',
                                        maxLength: {
                                            value: 240,
                                            message: 'Primary Contact Name should be maximum 240 characters',
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                            </div>
                            {errors.primaryContactName && (
                                <p className='error-form'>{errors.primaryContactName.message}</p>
                            )}
                            <label className='e-label-select mb-2'>Street 1</label>
                            <div
                                className={`e-float-input e-input-group ${
                                    errors.primaryContactStreet1 ? 'mb-1' : 'mb-3'
                                }`}
                                style={{ marginTop: '0px' }}
                            >
                                <input
                                    type='text'
                                    {...register('primaryContactStreet1', {
                                        maxLength: {
                                            value: 120,
                                            message: 'Primary Contact Street 1 should be maximum 120 characters',
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                            </div>
                            {errors.primaryContactStreet1 && (
                                <p className='error-form'>{errors.primaryContactStreet1.message}</p>
                            )}
                            <label className='e-label-select mb-2'>Street 2</label>
                            <div
                                className={`e-float-input e-input-group mb-3`}
                                style={{ marginTop: '0px' }}
                            >
                                <input
                                    type='text'
                                    {...register('primaryContactStreet2', {
                                        maxLength: {
                                            value: 120,
                                            message: 'Primary Contact Street 2 should be maximum 120 characters',
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                            </div>
                            {errors?.primaryContactStreet2?.message && (
                                <p className='error-form'>{errors.primaryContactStreet2.message}</p>
                            )}
                            <label className='e-label-select mb-2'>City</label>
                            <div
                                className={`e-float-input e-input-group ${errors.primaryContactCity ? 'mb-1' : 'mb-3'}`}
                                style={{ marginTop: '0px' }}
                            >
                                <input
                                    type='text'
                                    {...register('primaryContactCity', {
                                        maxLength: {
                                            value: 50,
                                            message: 'Primary Contact City should be maximum 50 characters',
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                            </div>
                            {errors.primaryContactCity && (
                                <p className='error-form'>{errors.primaryContactCity.message}</p>
                            )}
                            <Row>
                                <Col md={6}>
                                    <div className={`${errors.primaryContactState ? 'mb-1' : 'mb-3'}`}>
                                        <label className='e-label-select mb-2'>State</label>
                                        <MaskedTextBoxComponent
                                            mask='LL'
                                            {...register('primaryContactState', {
                                                maxLength: {
                                                    value: 50,
                                                    message: 'Primary Contact State should be maximum 50 characters',
                                                },
                                            })}
                                        />
                                        <span className='e-float-line'></span>
                                    </div>
                                    {errors.primaryContactState && (
                                        <p className='error-form'>{errors.primaryContactState.message}</p>
                                    )}
                                </Col>
                                <Col md={6}>
                                    <div>
                                        <label className='e-label-select mb-2'>Zip</label>
                                        <div
                                            className={`e-float-input e-input-group ${
                                                errors.primaryContactZipCode ? 'mb-1' : 'mb-3'
                                            }`}
                                            style={{ marginTop: '0px' }}
                                        >
                                            <input
                                                type='text'
                                                {...register('primaryContactZipCode', {
                                                    pattern: {
                                                        value: /^\d{5}([\-]?\d{4})?$/,
                                                        message:
                                                            'Incorrect input. Should be either xxxxx or xxxxx-xxxx',
                                                    },
                                                })}
                                            />
                                            <span className='e-float-line'></span>
                                        </div>
                                        {errors.primaryContactZipCode && (
                                            <p className='error-form'>{errors.primaryContactZipCode.message}</p>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={8}>
                                    <div className={`${errors.primaryContactPhone ? 'mb-1' : 'mb-3'}`}>
                                        <label className='e-label-select mb-2'>Phone</label>
                                        <MaskedTextBoxComponent
                                            mask='(000) 000-0000'
                                            {...register('primaryContactPhone', {
                                                pattern: {
                                                    value: /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                                                    message: 'Incorrect format phone number (123)123-1234',
                                                },
                                            })}
                                        />
                                        <span className='e-float-line'></span>
                                    </div>
                                    {errors.primaryContactPhone && (
                                        <p className='error-form'>{errors.primaryContactPhone.message}</p>
                                    )}
                                </Col>
                                <Col md={4}>
                                    <label className='e-label-select mb-2'>Ext</label>
                                    <div
                                        className={`e-float-input e-input-group mb-3`}
                                        style={{ marginTop: '0px' }}
                                    >
                                        <input
                                            type='text'
                                            {...register('primaryContactExtn', {
                                                maxLength: {
                                                    value: 10,
                                                    message:
                                                        'Primary Contact Phone Extension should be maximum 10 characters',
                                                },
                                            })}
                                        />
                                        <span className='e-float-line'></span>
                                    </div>
                                    {errors.primaryContactExtn && (
                                        <p className='error-form'>{errors.primaryContactExtn.message}</p>
                                    )}
                                </Col>
                            </Row>
                            <label className='e-label-select mb-2 d-flex justify-content-between'>
                                <span>Email</span>
                                <small className='text-muted'>100 characters max</small>
                            </label>
                            <div
                                className={`e-float-input e-input-group ${
                                    errors.primaryContractEmail ? 'mb-1' : 'mb-3'
                                }`}
                                style={{ marginTop: '0px' }}
                            >
                                <input
                                    type='email'
                                    maxLength={100}
                                    {...register('primaryContractEmail', {
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                            message: 'Email is not valid',
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                            </div>
                            {errors.primaryContractEmail && (
                                <p className='error-form'>{errors.primaryContractEmail.message}</p>
                            )}

                            <div
                                className='mb-3'
                                style={{ marginBottom: 10 }}
                            >
                                <label className='e-label-select mb-2'>Fax</label>
                                <MaskedTextBoxComponent
                                    mask='(000) 000-0000'
                                    {...register('primaryContractFax', {
                                        pattern: {
                                            value: /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                                            message: 'Fax is not valid',
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                                {errors.primaryContractFax && (
                                    <p className='error-form'>{errors.primaryContractFax.message}</p>
                                )}
                            </div>
                        </div>
                    </Col>
                    <Col md={6}>
                        <label style={{ marginLeft: '15px' }}>
                            <b>Secondary Contact</b>
                        </label>
                        <div
                            className='mt-3'
                            style={{
                                borderLeft: '1px solid gray',
                                paddingLeft: '15px',
                                height: '629px',
                            }}
                        >
                            <label className='e-label-select mb-2'>Title</label>
                            <div
                                className={`e-float-input e-input-group mb-3`}
                                style={{ marginTop: '0px' }}
                            >
                                <input
                                    type='text'
                                    {...register('secondaryContactTitle', {
                                        maxLength: {
                                            value: 240,
                                            message: 'Secondary Contact Title should be maximum 240 characters',
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                            </div>
                            {errors?.secondaryContactTitle?.message && (
                                <p className='error-form'>{errors.secondaryContactTitle.message}</p>
                            )}
                            <label className='e-label-select mb-2'>Name</label>
                            <div
                                className={`e-float-input e-input-group mb-3`}
                                style={{ marginTop: '0px' }}
                            >
                                <input
                                    type='text'
                                    {...register('secondaryContactName', {
                                        maxLength: {
                                            value: 240,
                                            message: 'Secondary Contact Name should be maximum 240 characters',
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                            </div>
                            {errors?.secondaryContactName?.message && (
                                <p className='error-form'>{errors.secondaryContactName.message}</p>
                            )}
                            <div className='no-padding mb-1'>
                                <label className='e-label-select mb-2'>Phone</label>
                                <MaskedTextBoxComponent
                                    mask='(000) 000-0000'
                                    {...register('secondaryContactPhone', {
                                        pattern: {
                                            value: /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                                            message: 'Incorrect format phone number (123)123-1234',
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                            </div>
                            {errors.secondaryContactPhone && (
                                <p className='error-form'>{errors.secondaryContactPhone.message}</p>
                            )}
                            <div
                                className='no-padding mb-3'
                                style={{ marginTop: '-4px' }}
                            >
                                <label className='e-label-select mb-2'>Fax</label>
                                <MaskedTextBoxComponent
                                    mask='(000) 000-0000'
                                    {...register('secondaryContactFax', {
                                        pattern: {
                                            value: /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                                            message: 'Fax is not valid',
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                                {errors.secondaryContactFax && (
                                    <p className='error-form'>{errors.secondaryContactFax.message}</p>
                                )}
                            </div>
                            <label className='e-label-select mb-2 d-flex justify-content-between'>
                                <span>Email</span>
                                <small className='text-muted'>100 characters max</small>
                            </label>
                            <div
                                className={`e-float-input e-input-group mb-1`}
                                style={{ marginTop: '-3px' }}
                            >
                                <input
                                    type='text'
                                    maxLength={100}
                                    {...register('secondaryContactEmail', {
                                        pattern: {
                                            value: /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/,
                                            message: 'Email is not valid',
                                        },
                                    })}
                                />
                                <span className='e-float-line'></span>
                            </div>
                            {errors.secondaryContactEmail && (
                                <p className='error-form'>{errors.secondaryContactEmail.message}</p>
                            )}
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col md='12'>
                        <div
                            className='no-padding mb-3'
                            style={{ marginTop: 10 }}
                        >
                            <label className='e-label-select mb-2'>Comments</label>
                            <div
                                className={`e-float-input e-input-group`}
                                style={{ marginTop: '0px' }}
                            >
                                <textarea
                                    rows={4}
                                    {...register('comments')}
                                />
                                <span className='e-float-line'></span>
                            </div>
                        </div>
                    </Col>
                </Row>
            </form>
        </DisabledWrapper>
    );
};
